<template>
  <div class="cursor">
    <svg
      class="cursor__rect"
      viewBox="0 0 250 250"
    >
      <rect
        width="180" height="180"
        x="35" y="35"
        rx="60"
      />
    </svg>
    <div
      class="cursor__pointer"
      :style="{
        transform: `translate3d(${this.interMouse.x}px, ${this.interMouse.y}px, 0)`,
      }"
    >
      <svg
        class="pointer__dot"
        viewBox="0 0 10 10"
      >
        <circle
          cx="5" cy="5" r="4" fill="white"
        />
      </svg>

      <svg
        class="pointer__drag"
        x="0px" y="0px"
        viewBox="0 0 55 45"
      >
        <polygon points="54,22.5 48,28.5 48,16.5 "/>
        <path d="M27.5,1V44"/>
        <polygon points="1,22.5 7,28.5 7,16.5 "/>
      </svg>
    </div>
    <div
      class="cursor__text"
      :style="{
        transform: `translate3d(${this.interMouse.x}px, ${this.interMouse.y}px, 0)`,
      }"
    >
      <span>{{ cursorContent }}</span>
    </div>
  </div>
</template>

<script>
import anime from 'animejs';

import cursor from '@/scripts/cursor';
import { isTouchDevice } from '@/scripts/utils';

export default {
  name: 'AppCursor',
  data() {
    return {
      mouse: { x: 0, y: 0 },
      interMouse: { x: 0, y: 0 },
      rect: undefined,
      rectSvg: undefined,
      dot: undefined,
      state: 'rect',
      cursorContent: '',
    };
  },
  mounted() {
    if (!isTouchDevice()) {
      this.rect = this.$el.querySelector('.cursor__rect');
      this.rectSvg = this.$el.querySelector('.cursor__rect rect');
      this.dot = this.$el.querySelector('.pointer__dot');
      this.textEl = this.$el.querySelector('.cursor__text span');

      anime.set(this.rectSvg, { rotate: '45', transformOrigin: '50% 50%' });
      anime.set(this.textEl, { scale: 0, transformOrigin: '50% 50%' });
      cursor.init(this.rect, (lerpVal, val) => this.render(lerpVal, val));

      this.addEvents();
    }
  },
  methods: {
    addEvents() {
      this.$eventHub.$on('morphCursor', type => this.morph(type));
      this.$eventHub.$on('textCursor', content => this.text(content));
    },
    lerp(a, b, n) {
      return ((1 - n) * a) + (n * b);
    },
    render(lerpVal, val) {
      const lerpedX = this.lerp(val.x, this.interMouse.x, 0.85);
      const lerpedY = this.lerp(val.y, this.interMouse.y, 0.85);

      this.interMouse.x = lerpedX;
      this.interMouse.y = lerpedY;
    },
    morph(type) {
      const rx = type === 'rect' ? 60 : 106;
      const scale = type === 'rect' ? 1 : type === 'circle' ? 0.75 : 0.9; // eslint-disable-line

      if (type === 'drag' && !this.$el.classList.contains('cursor--drag')) {
        this.$el.classList.add('cursor--drag');
      } else if (this.$el.classList.contains('cursor--drag')) {
        this.$el.classList.remove('cursor--drag');
      }

      if (this.state === 'text') {
        this.state = type;

        anime.remove(this.dot);
        anime({
          targets: this.dot,
          scale: 1,
          translateX: -5,
          translateY: -5,
          transformOrigin: ['50% 50%', '50% 50%'],
          duration: 800,
        });
        anime.remove(this.textEl);
        anime({
          targets: this.textEl,
          scale: 0,
          opacity: 0,
          duration: 800,
        });
      }

      anime.remove(this.rectSvg);
      anime({
        targets: this.rectSvg,
        rx,
        scale,
        transformOrigin: ['50% 50%', '50% 50%'],
        duration: 800,
      });
    },
    text(content) {
      this.state = 'text';
      if (this.cursorContent !== content) {
        this.cursorContent = content;
      }

      anime.remove(this.dot);
      anime({
        targets: this.dot,
        scale: 0,
        translateX: -5,
        translateY: -5,
        transformOrigin: ['50% 50%', '50% 50%'],
        duration: 800,
      });
      anime.remove(this.rectSvg);
      anime({
        targets: this.rectSvg,
        rx: 106,
        scale: 1.5,
        duration: 800,
      });
      anime.remove(this.textEl);
      anime({
        targets: this.textEl,
        scale: 1,
        opacity: 1,
        duration: 800,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cursor {
  position: fixed;
  top: 0;
  left: 0;
  width: 200px;

  mix-blend-mode: hard-light;
  pointer-events: none;
  z-index: 50;

  @include mq($and: $is-touch-device) {
    display: none;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 200px;
    margin-left: -100px;

    overflow: visible;
  }

  rect {
    fill: var(--color-cursor);
    transition: fill 0.8s $outCirc;

    @at-root [data-city="toronto"] & {
      fill: var(--color-cursor-dark);
    }
  }

  &__pointer {
    .pointer__dot {
      width: 10px;
      height: 10px;

      transform: translateX(-5px) translateY(-5px);
      transition: transform 0.4s $outCirc;
    }

    .pointer__drag {
      width: 50px;
      height: 50px;

      transform: translateX(-25px) translateY(-25px) scale(0);
      transition: transform 0.4s $outCirc;

      polygon,
      path {
        fill: none;
        stroke: var(--color-headline-light);
        stroke-width: 1.2;

        @at-root [data-city="toronto"] & {
          stroke: var(--color-bg);
        }
      }
    }
  }

  &.cursor--drag {
    rect {
      fill: var(--color-headline-dark);

      @at-root [data-city="toronto"] & {
        fill: var(--color-headline-light);
      }
    }

    .pointer__dot {
      transform: translateX(-5px) translateY(-5px) scale(0);
    }

    .pointer__drag {
      transform: translateX(-25px) translateY(-25px) scale(1);
    }
  }

  .cursor__text {
    display: inline-block;
    position: absolute;
    width: 0;

    text-align: center;
    white-space: nowrap;
    display: flex;
    justify-content: center;
    align-items: flex-start;

    span {
      display: inline-block;
      position: relative;
      margin-top: -10px;
    }
  }
}
</style>
